import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  Highlight,
  HighlightType,
  HighlightVariant,
  Icon,
} from '@node-space/ui-components'
import { LoadingIndicator } from 'components/LoadingIndicator'
import { MERCHANT_RETAINED_EARNINGS_PERCENTAGE } from '../../constants/const'
import { formatWholePercentage } from '../../utils/utils'
import { EarnEmbeddedLabelValueRow } from './EarnEmbeddedLabelValueRow'

export interface EarnEmbeddedDetailsCustomerSummaryProps {
  customerEarnRate: string
  customerEarningBalance: string
  customerPaidAmount: string
  epcTakesEarn: boolean
  isStatisticsLoading?: boolean
}

export const EarnEmbeddedDetailsCustomerSummary = ({
  customerEarnRate,
  customerEarningBalance,
  customerPaidAmount,
  epcTakesEarn,
  isStatisticsLoading,
}: EarnEmbeddedDetailsCustomerSummaryProps) => {
  const { t } = useTranslation()
  const retainedEarningsPercentage = formatWholePercentage(MERCHANT_RETAINED_EARNINGS_PERCENTAGE)

  return (
    <Card
      variant="static"
      full
      borderRadius={12}
      testId="earn-details-customer-summary"
      className="bg-surface-interactive"
    >
      <Box flexCol gap={4}>
        <Box flex gap={12} align="center">
          <Box className="text-foreground-action-secondary">
            <Icon name="HandCoins" size="large" />
          </Box>
          <p className="text-body-regular text-foreground-subdued">
            {t('earn.totalCustomerFundsEarning')}
          </p>
        </Box>

        <Box flexCol gap={4}>
          <Box flex gap={8} align="center">
            {isStatisticsLoading ? (
              <LoadingIndicator testId="earn-total-customer-funds-earning-skeleton" />
            ) : (
              <h1 className="text-h1 text-foreground-prominent">{customerEarningBalance}</h1>
            )}
            <Highlight type={HighlightType.Feature} variant={HighlightVariant.Emphasis}>
              {t('earn.earningAtRate', { rate: customerEarnRate })}
            </Highlight>
          </Box>
          {epcTakesEarn && (
            <p className="text-body-regular text-foreground-subdued">
              {t('earn.acrossOptedInCustomerBalances')}
            </p>
          )}
        </Box>

        <Box className="w-full border-t border-stroke-static mt-2 mb-2" />

        {epcTakesEarn ? (
          <EarnEmbeddedLabelValueRow
            testId="earn-distributed-earnings-across-customers"
            label={t('earn.distributedEarningsAcrossCustomersLabel')}
            value={customerPaidAmount}
          />
        ) : (
          <EarnEmbeddedLabelValueRow
            testId="earn-retained-earnings-percentage"
            label={t('earn.retainedCustomerEarningsPercentage')}
            value={retainedEarningsPercentage}
          />
        )}
      </Box>
    </Card>
  )
}
